import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { __ } from '@wordpress/i18n';
// components;
import Button from "../../../common/components/inputs/Button";
import TextField from "../../../common/components/inputs/TextField";
import Title from "../../../common/components/Title";
import SubTitle from "../../../common/components/SubTitle";
import Table from "./components/Table";
import Pagination from "../../../common/components/Pagination";

const Tags = ({ page }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [tags, setTags] = useState([]);
    const [flatTags, setFlatTags] = useState([]);
    const [tagsData, setTagsData] = useState({ tags: [{ tag_name: '', tag_slug: '' }] });
    const [editingTag, setEditingTag] = useState(null);
    const [totalPage, setTotalPage] = useState(1);
    const [postPerPage, setPostPerPage] = useState(10);

    useEffect(() => {
        fetchData();
    }, []);

    const generateSlug = (text) => {
        return text
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^a-z0-9\-]/g, '');
    };

    const flattenTags = (tags) => {
        let flatList = [];

        tags.forEach((tag) => {
            flatList.push({
                id: tag.id,
                name: tag.name,
                slug: tag.slug,
            });

            if (tag.children && tag.children.length > 0) {
                flatList = flatList.concat(flattenTags(tag.children));
            }
        });

        return flatList;
    };

    const fetchData = async () => {
        setIsLoading(true);
        
        try {
            const response = await fetch(`${EASYCOMMERCE.rest_base}/products/tags?page=${page}&per_page=${postPerPage}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': EASYCOMMERCE.nonce,
                },
            });
            const data = await response.json();
            setTags(data.data.tags);

            const flattened = flattenTags(data.data.tags);
            setFlatTags(flattened);
            // console.log('data.data.total_pages', data.data.pagination.total_pages);
            setTotalPage(data.data.pagination.total_pages);

        } catch (error) {
            toast.error('Failed to fetch tags.');
        }finally {
            setIsLoading(false); 
        } 
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'tag_name') {
            const newSlug = generateSlug(value);
            setTagsData((prev) => ({
                ...prev,
                tags: [{ ...prev.tags[0], tag_name: value, tag_slug: newSlug  }]
            }));
        }
    };

    const handleEditTag = (id) => {
        easycommerce_modal(true);
    
        const tag  = findTagById(tags, id);
        const flatEntry = flatTags.find(fc => fc.id === id);
    
        if (tag && flatEntry) {
            setTagsData({
                tags: [{
                    tag_name: tag.name,
                    tag_slug: tag.slug,
                }]
            });
    
            setEditingTag(id);
        }
        easycommerce_modal(false);
    };
    
    const findTagById = (tags, id) => {
        for (let tag of tags) {
            if (tag.id === id) return tag;
            if (tag.children && tag.children.length > 0) {
                const found = findTagById(tag.children, id);
                if (found) return found;
            }
        }
        return null;
    };

    const handleSubmit = async () => {
        const payload = {
            name: tagsData.tags[0].tag_name,
            slug: tagsData.tags[0].tag_slug,
        };

        if (!payload.name.trim() || !payload.slug.trim()) {
            toast.error('All fields are required!');
            return;
        }
        const method = editingTag ? 'PUT' : 'POST';

        try {
            const url = editingTag
                ? `${EASYCOMMERCE.rest_base}/products/tags/${editingTag}`
                : `${EASYCOMMERCE.rest_base}/products/tags`;

            easycommerce_modal(true);
            const response = await fetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': EASYCOMMERCE.nonce,
                },
                body: JSON.stringify(payload)
            });

            const data = await response.json();

            if (data.success) {
                toast.success(`Tag ${editingTag ? 'updated' : 'created'} successfully`);
                fetchData();
                setTagsData({ tags: [{ tag_name: '', tag_slug: '' }] });
                setEditingTag(null);
                easycommerce_modal(false);
            } else {
                toast.error(data.data.message || 'Operation failed');
                easycommerce_modal(false);
            }
        } catch (error) {
            toast.error('Operation failed');
        }
    };

    const handleCancel = () => {
        easycommerce_modal(true);
 
        setTagsData({ tags: [{ tag_name: '', tag_slug: '' }] });
        setEditingTag(null);
     
        setTimeout(() => {
            easycommerce_modal(false);
        }, 100); 
    };
    
    return (
        <>
            <div className="product-panel-title mb-4">
                <h3>Tags</h3>
            </div>
            <div className="grid min-[1400px]:grid-cols-10 min-[1280px]:grid-cols-12 gap-6 items-start">
                <div className="bg-white min-[1400px]:col-span-6 min-[1280px]:col-span-12 w-full self-start border border-solid border-ec-table-stock rounded-xl">
                    <div className="flex items-center border-b pl-6 py-3.5">
                        <Title title="Tags List" />
                    </div>

                    <div className="p-6">
                        <Table
                            tags={flatTags}
                            fetchData={fetchData}
                            onEdit={handleEditTag}
                            isLoading={isLoading}
                        />
                    </div>
                    {totalPage > 1 && (
                        <Pagination
                            baseSlug="tags"
                            current={page}
                            total={totalPage}
                        />
                    )}
                </div>

                {/* Form Section */}
                <div className="bg-white min-[1400px]:col-span-4 min-[1280px]:col-span-12 w-full self-start border border-solid border-ec-table-stock rounded-xl">
                    <div className="flex items-center border-b pl-6 py-3.5">
                        <Title title={editingTag ? 'Edit Tag: ' + tagsData.tags[0].tag_name : "Add New Tag"} />
                    </div>

                    <div className="p-6 flex flex-col gap-4">
                        <div className='flex flex-col gap-2'>
                            <SubTitle SubTitle="Name" notice="It'll be used to filter products" />
                            <TextField 
                                name="tag_name" 
                                value={tagsData.tags[0].tag_name}
                                onChange={handleChange} 
                                placeholder="Enter Name" 
                                className="h-ec-input" 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <SubTitle SubTitle="Slug" notice="URL-friendly slug of the tag" />

                            <div className="h-ec-input rounded-lg font-inter text-[14px] leading-[20px] border border-ec-table-stock placeholder-ec-placeholder hover:border-ec-primary focus-within:border-ec-primary focus-within:outline-none focus-within:[box-shadow:0_0_0_4px_#F3F0FF] transition-colors duration-300 ease-in-out overflow-hidden flex ">
                                <div className="h-ec-input flex items-center justify-center text-ec-light-black pl-4">
                                    {EASYCOMMERCE.home_url}/shop/tags/
                                </div>
                                <input
                                    type="text"
                                    name="tag_slug"
                                    className="h-ec-input border-none outline-none shadow-none p-0 text-ec-body font-inter text-[14px] leading-[20px]"
                                    // placeholder={__('Enter Slug', 'easycommerce')}
                                    value={tagsData.tags[0].tag_slug}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        setTagsData(prev => ({
                                        ...prev,
                                        tags: [{ ...prev.tags[0], tag_slug: val }]
                                        }));
                                    }}
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between mt-6">
                            {editingTag ? (
                                <Button
                                    className="h-[37px] mt-[15px] easycommerce-underline-button"
                                    value="Cancel"
                                    onClick={handleCancel} 
                                />
                            ) : (
                                <div /> 
                            )}
                            <Button
                                className="easycommerce-outline-button"
                                value={editingTag ? "Update Tag" : "Create Tag"}
                                onClick={handleSubmit}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Tags;